import { BarChartOutlined, DownloadOutlined, InfoCircleOutlined, LineChartOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Button, Radio, Select, Tooltip, Typography } from 'antd'
import { useAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { UserAvatar } from '@/components/user-name.component'
import { UserDetailTable } from '@/components/user-resource-dashboard/detail-table/user-detail-table'
import { DashboardLineChart } from '@/components/user-resource-dashboard/user-resource-dashboard-chart/dashboard-chart-line'
import { departmentsMapAtom } from '@/modules/department'
import { usersAtom } from '@/modules/user'
import { Project } from '@/schemas'

export const ProjectTrendPeopleDashboardCard: React.FC<{
  selfId: string
  project: Project
}> = ({ selfId, project }) => {
  const { t } = useTranslation()

  const [users] = useAtom(usersAtom)
  const [departmentsMap] = useAtom(departmentsMapAtom)

  const [selected, setSelected] = React.useState()
  const [chartType, setChartType] = React.useState<'line' | 'interval'>('line')
  const [selectOptions, setSelectOptions] = React.useState<any[]>([])

  const startDate = new URLSearchParams(window.location.search).get('startDate')
  const endDate = new URLSearchParams(window.location.search).get('endDate')

  const { data: trendPeopleData, run: runTrendPeopleData } = useRequest(
    (email?: string) => ({
      url: '/api/work-resource/statistics/project/trend/people/data',
      params: {
        startDate,
        endDate,
        selfId,
        emails: [email],
      },
    }),
    {
      manual: true,
      initialData: { chart: [], list: [] },
    }
  )

  const { data: trendPeopleList, run: runTrendPeopleList } = useRequest(
    {
      url: '/api/work-resource/statistics/project/trend/people/list',
      params: {
        startDate,
        endDate,
        selfId,
      },
    },
    {
      manual: true,
      initialData: [],
    }
  )

  const { run: runDownloadExcel } = useRequest(
    {
      url: '/api/work-resource/statistics/project/trend/people/excel',
      method: 'POST',
      data: {
        startDate,
        endDate,
        selfId,
      },
      responseType: 'blob',
    },
    {
      manual: true,
      onSuccess: (res: any) => {
        saveAs(res, `${selfId}-${project.name}-人员资源明细-${startDate}-${endDate}.xlsx`)
      },
    }
  )

  const list = (trendPeopleList as any) || []

  const data = (trendPeopleData as any) || { chart: [], list: [] }

  React.useEffect(() => {
    if (startDate && endDate && selfId) {
      runTrendPeopleList()
    }
  }, [startDate, endDate, selfId])

  React.useEffect(() => {
    if (list.length > 0) {
      setSelectOptions(
        list!.map((u: any) => {
          const user = users.find((user) => user.email === u.email)
          // if (!user) return
          const email = user?.email || u.email
          const name = user?.name || u.email
          return (
            <Select.Option key={email} value={email} user={user} name={email}>
              <div className='flex'>
                <UserAvatar className='mr-8px' size='small' email={email} />
                <div className='text-xs leading-tight'>
                  <div>{name}</div>
                  <Typography.Text type='secondary'>
                    {user?.departmentIds
                      .map((id) => departmentsMap![id]?.name)
                      .filter(Boolean)
                      .join(',')}
                  </Typography.Text>
                </div>
              </div>
            </Select.Option>
          )
        })
      )
      list[0] ? setSelected({ value: list[0].email, name: list[0].email } as any) : setSelected(undefined)
    }
  }, [list])

  React.useEffect(() => {
    if (startDate && endDate && selfId) {
      selected ? runTrendPeopleData(selected['value']) : runTrendPeopleData(undefined)
    }
  }, [selected])

  return (
    <div className='flex mb-8'>
      <div className='flex-grow mr-16'>
        <div className='flex mb-4 justify-between'>
          <div className='flex font-medium text-lg gap-1 items-baseline'>
            {t(`人员视角`)}
            <Tooltip
              title={
                '默认展示时间范围内该项目工时最多的人，显示该人员所在的所有普通/自定义项目，项目分摊比例已纳入计算。'
              }
            >
              <InfoCircleOutlined className='cursor-pointer text-sm' size={14} />
            </Tooltip>
          </div>
          <div className='flex'>
            <Select
              className='mr-4 w-60'
              labelInValue
              maxTagCount={1}
              value={selected}
              onChange={(v) => setSelected(v)}
              showSearch
              showArrow
              optionFilterProp='name'
            >
              {selectOptions}
            </Select>
            <Radio.Group onChange={(e) => setChartType(e.target.value)} value={chartType}>
              <Radio.Button value='line'>
                <LineChartOutlined />
              </Radio.Button>
              <Radio.Button value='interval'>
                <BarChartOutlined />
              </Radio.Button>
            </Radio.Group>
          </div>
        </div>
        <DashboardLineChart
          data={data!.chart.map((c: any) => ({
            ...c,
            date: t('dashboard.xAxisLabel', { year: c.year, week: c.week }),
          }))}
          type={chartType}
        />
      </div>
      <div className='flex-shrink-0 w-90'>
        <div className='flex font-medium text-lg mb-4 gap-1 items-center justify-between'>
          <span className='flex gap-1 items-baseline'>
            {t(`人员资源明细`)}
            <Tooltip title={'时间范围内该项目的人员工时/时间范围内人员总工时'}>
              <InfoCircleOutlined className='cursor-pointer text-sm' size={14} />
            </Tooltip>
          </span>
          <Tooltip title={t('下载')}>
            <Button shape='circle' icon={<DownloadOutlined />} onClick={() => runDownloadExcel()} />
          </Tooltip>
        </div>
        <UserDetailTable list={data!.list as any} />
      </div>
    </div>
  )
}
